import { formatUSD, formatPct } from "@/lib/format";

const formatExpiry = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y.slice(2)}`;
};

export default function TraderCard({ trader }) {
  const positive = trader.pnl >= 0;
  const holdings = Object.entries(trader.holdings || {}).filter(([, q]) => q > 0);
  const options = (trader.options || []).filter((o) => o.contracts > 0);

  return (
    <div
      data-testid={`trader-card-${trader.id}`}
      className="bg-white p-6 lg:p-8 flex flex-col gap-5"
    >
      <div className="flex items-start gap-4">
        <div
          className="w-12 h-12 flex items-center justify-center font-display font-black text-white text-lg shrink-0"
          style={{ background: trader.color }}
        >
          {trader.name.split(" ")[0].slice(0, 1)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-lg tracking-tight leading-tight">
            {trader.name}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-arena-soft mt-1">
            {trader.provider} · {trader.model}
          </div>
        </div>
      </div>

      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft">
          Total value
        </div>
        <div className="font-mono font-bold text-2xl tabular-nums">
          {formatUSD(trader.total_value)}
        </div>
        <div className={`font-mono text-xs tabular-nums mt-1 ${positive ? "text-profit" : "text-loss"}`}>
          {formatUSD(trader.pnl, { sign: true })} ({formatPct(trader.pnl_pct)})
        </div>
      </div>

      <div className="grid grid-cols-3 gap-px bg-arena-line border border-arena-line">
        <div className="bg-white p-3">
          <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-arena-soft">Cash</div>
          <div className="font-mono font-bold text-sm tabular-nums">{formatUSD(trader.cash)}</div>
        </div>
        <div className="bg-white p-3">
          <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-arena-soft">Stocks</div>
          <div className="font-mono font-bold text-sm tabular-nums">
            {formatUSD(trader.holdings_value)}
          </div>
        </div>
        <div className="bg-white p-3">
          <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-arena-soft">Options</div>
          <div className="font-mono font-bold text-sm tabular-nums">
            {formatUSD(trader.options_value || 0)}
          </div>
        </div>
      </div>

      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft mb-2">
          Stock positions ({holdings.length})
        </div>
        {holdings.length === 0 ? (
          <div className="text-xs text-arena-soft font-mono py-2">— no stock positions</div>
        ) : (
          <div className="border-t border-arena-line">
            {holdings.map(([sym, qty]) => (
              <div key={sym} className="flex items-center justify-between py-2 border-b border-arena-line/60 text-sm font-mono tabular-nums">
                <span className="font-bold">{sym}</span>
                <span className="text-arena-soft">{qty} sh</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft mb-2">
          Option positions ({options.length})
        </div>
        {options.length === 0 ? (
          <div className="text-xs text-arena-soft font-mono py-2">— no open options</div>
        ) : (
          <div className="border-t border-arena-line">
            {options.map((o) => (
              <div key={o.id} className="flex items-center justify-between py-2 border-b border-arena-line/60 text-xs font-mono tabular-nums gap-2">
                <span className="flex items-center gap-1.5 min-w-0">
                  <span
                    className={`px-1.5 py-[1px] text-[9px] font-bold tracking-widest text-white ${o.type === "call" ? "bg-profit" : "bg-loss"}`}
                  >
                    {o.type.toUpperCase()}
                  </span>
                  <span className="font-bold truncate">{o.underlying}</span>
                  <span className="text-arena-soft">${o.strike.toFixed(0)}</span>
                  <span className="text-arena-mute text-[10px]">{formatExpiry(o.expiry)}</span>
                </span>
                <span className="text-arena-soft shrink-0">× {o.contracts}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
